import * as yup from 'yup';
import type { ListingSubtype, ListingType } from 'app/types/listing';

export const UserLevel = {
  EXPLORER: 'explorer',
  CONTRIBUTOR: 'contributor',
  SHARER: 'sharer',
  SUPER_SHARER: 'superSharer',
} as const;
export type UserLevelType = (typeof UserLevel)[keyof typeof UserLevel];

export const AmbassadorStatus = {
  IDENTIFIED: 'identified',
  INTERESTED: 'interested',
  ONBOARDING_BOOKED: 'onboarding_booked',
  TRIAL_PERIOD: 'trial_period',
  CONFIRMED_AMBASSADOR: 'confirmed_ambassador',
  INACTIVE: 'inactive',
  DECLINED: 'declined',
} as const;
export type AmbassadorStatusType = (typeof AmbassadorStatus)[keyof typeof AmbassadorStatus];

export const Currency = {
  CAD: 'CAD',
  EUR: 'EUR',
  USD: 'USD',
} as const;
export type CurrencyType = (typeof Currency)[keyof typeof Currency];

export const DistanceUnit = {
  KM: 'km',
  MI: 'mi',
} as const;
export type DistanceUnitType = (typeof DistanceUnit)[keyof typeof DistanceUnit];

const customTabFilters = yup.object({
  type: yup.string<ListingType>().required(),
  subtypes: yup.array(yup.string<ListingSubtype>().required()).notRequired(),
  hideFoundRequests: yup.boolean().notRequired(),
  dismissedOnly: yup.boolean().notRequired(),
});
export type CustomTabFilters = yup.InferType<typeof customTabFilters>;

export const userSchema = yup.object({
  id: yup.string().uuid().required(),
  createdAt: yup.date().required(),
  lastSeenAt: yup.date().optional().nullable(),
  acceptedGuidelineDate: yup.date().optional().nullable(),
  email: yup.string().email().required(),
  referralURL: yup.string().url().required(),
  status: yup.mixed<'complete' | 'pending' | 'new'>().required(),
  verified: yup.boolean().required(),
  level: yup.mixed<UserLevelType>().notRequired(),
  referralPrizeAvailable: yup.boolean().optional(),
  referralPrizeObtained: yup.boolean().optional(),
  referralPrize202506Available: yup.boolean().optional(),
  referralPrize202506Obtained: yup.boolean().optional(),
  trustPoints: yup.number().notRequired(),
  insured: yup.boolean().notRequired(),
  canBeInsured: yup.boolean().notRequired(),
  currency: yup.mixed<CurrencyType>().notRequired(),
  distanceUnit: yup.mixed<DistanceUnitType>().notRequired(),
  region: yup.mixed<'CA' | 'US'>().nullable().notRequired(),
  isSuper: yup.boolean().notRequired(),
  isAmbassador: yup.boolean().notRequired(),
  ambassadorStatus: yup.mixed<AmbassadorStatusType>().notRequired(),
  responseRate: yup.number().notRequired(),
  averageResponseTime: yup.number().notRequired(),
  canPublishDonations: yup.boolean().notRequired(),
  canReceiveDesjardinsPromo: yup.boolean().notRequired(),
  type: yup.string().notRequired(),
  sub: yup.string().notRequired(),
  recentSearches: yup.array(yup.string().required()).notRequired(),
  hasInterests: yup.boolean().notRequired(),
  offersCount: yup.number().notRequired(),
  featureFlags: yup
    .object({
      // Note: widened to string[] to support dynamic (DB) flag keys e.g. 'group_chat' in
      // addition to static FeatureFlag enum values. Runtime behavior unchanged (server sends
      // merged list either way; schema used only for InferType here, not .validate on responses).
      enabled: yup.array(yup.string().required()).required(),
      disabled: yup.array(yup.string().required()).required(),
      customTabs: yup
        .array(
          yup.object({
            name: yup.string().required(),
            filters: customTabFilters.required(),
            hideFilterButton: yup.boolean().notRequired(),
            i18nKey: yup.string().notRequired(),
          }),
        )
        .notRequired(),
    })
    .notRequired(),
  metas: yup
    .array(
      yup.object({
        name: yup.string().required(),
        value: yup.mixed().required(),
      }),
    )
    .notRequired(),
  metaPrompts: yup
    .array(
      yup.object({
        metaName: yup.string().required(),
        prompt: yup.string().required(),
        options: yup.string().required(),
      }),
    )
    .notRequired(),
  /** Returned by the API only when fetching the current user. True if the user has at least one accepted/returned/completed loan. */
  hasShared: yup.boolean().notRequired(),
  /**
   * Returned by the API only when fetching the current user.
   * - When `hasShared` is false: number of consecutive days with at least one daily action (Duolingo-style, allows a single missed day).
   * - When `hasShared` is true: number of consecutive months with at least one own loan (same single-skip rule).
   */
  numStreak: yup.number().notRequired(),
  /** Returned by the API only when fetching the current user. True if the user did not contribute yesterday (about to lose their streak). */
  streakWarning: yup.boolean().notRequired(),
  streakMissedDaySlots: yup.array().of(yup.number().integer().min(1).max(7).required()).notRequired(),
  streakCompletedDaySlots: yup.array().of(yup.number().integer().min(1).max(7).required()).notRequired(),
  streakCurrentDaySlot: yup.number().integer().min(1).max(7).notRequired(),
  loginAsId: yup.string().uuid().notRequired(),
});
